import { computed, ref } from "vue";
import { defineStore } from "pinia";
import type { PracticeBand } from "common/practiceBands";
import type { PracticeCurriculumId } from "common/practiceCurricula";
import {
  emptyMasteryState,
  endSubjectPath,
  isSubjectProficient,
  parseMasteryState,
  pickNextSkill,
  pickNextTemplate,
  recordCheck,
  skillStateRows,
  skillsFromRows,
  startSubjectPath,
  subjectAttemptCount,
  subjectMasteryKey,
  subjectPercent,
  type PracticeMasteryState,
  type PracticeSkillStateRow,
} from "common/practiceMastery";
import {
  applyAdaptiveStep,
  type AdaptiveDecision,
} from "common/practiceAdaptive";
import type { PracticeDifficulty } from "common/practiceQuestionTemplates";
import useApiHelper from "../../helpers/apiHelper";
import { PRACTICE_QUESTION_TEMPLATES } from "common/practiceQuestionTemplates";
import { practiceSkillIdsForOffering } from "common/practiceCatalog";
import { getOrCreateGuestId } from "../../helpers/guestPracticeHelper";
import { useUserStore } from "./userStore";
import { usePracticeCurriculumStore } from "./practiceCurriculumStore";

const STORAGE_PREFIX = "mathboard-practice-mastery:";
const IMPORTED_PREFIX = "mathboard-practice-mastery-imported:";

function ownerKey(): string {
  try {
    const uuid = useUserStore().getCurrentUser()?.uuid?.trim();
    if (uuid) return `user:${uuid}`;
  } catch {
    /* pinia not ready */
  }
  try {
    return `guest:${getOrCreateGuestId()}`;
  } catch {
    return "guest:local";
  }
}

function storageKey(owner: string): string {
  return `${STORAGE_PREFIX}${owner}`;
}

function readState(
  owner: string,
  curriculumId: PracticeCurriculumId,
): PracticeMasteryState {
  try {
    const raw = localStorage.getItem(storageKey(owner));
    if (!raw) return emptyMasteryState(curriculumId);
    return parseMasteryState(JSON.parse(raw), curriculumId);
  } catch {
    return emptyMasteryState(curriculumId);
  }
}

function writeState(owner: string, state: PracticeMasteryState) {
  try {
    localStorage.setItem(storageKey(owner), JSON.stringify(state));
  } catch {
    /* private mode */
  }
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const usePracticeMasteryStore = defineStore("practiceMastery", () => {
  const curriculumStore = usePracticeCurriculumStore();
  const owner = ref(ownerKey());
  const state = ref<PracticeMasteryState>(
    readState(owner.value, curriculumStore.curriculumId),
  );
  let syncedFor: string | null = null;
  let syncingFor: string | null = null;
  let syncing: Promise<void> | null = null;

  function persist() {
    writeState(owner.value, state.value);
  }

  function syncOwner() {
    const next = ownerKey();
    if (next === owner.value) return;
    owner.value = next;
    state.value = readState(next, curriculumStore.curriculumId);
  }

  function withCurriculum() {
    if (state.value.curriculumId === curriculumStore.curriculumId) return;
    state.value = { ...state.value, curriculumId: curriculumStore.curriculumId };
    persist();
  }

  function subjectRecord(band: PracticeBand, subject: string) {
    return state.value.subjects[subjectMasteryKey(band, subject)];
  }

  const percentFor = computed(() => {
    return (band: PracticeBand, subject: string) =>
      subjectPercent(state.value, band, subject);
  });

  function startPath(band: PracticeBand, subject: string) {
    syncOwner();
    withCurriculum();
    state.value = startSubjectPath(state.value, band, subject);
    persist();
  }

  function completePath(band: PracticeBand, subject: string) {
    state.value = endSubjectPath(state.value, band, subject);
    persist();
  }

  function recordOutcome(input: {
    skillId?: string;
    tags: string[];
    band: PracticeBand;
    subject: string;
    correct: boolean;
    itemId: string;
    difficulty?: PracticeDifficulty;
    generated?: boolean;
  }): AdaptiveDecision | null {
    syncOwner();
    withCurriculum();
    state.value = recordCheck(state.value, input);
    const skillId = input.skillId?.trim();
    let decision: AdaptiveDecision | null = null;
    if (skillId) {
      const adapted = applyAdaptiveStep(state.value, skillId);
      state.value = adapted.state;
      decision = adapted.decision;
    }
    persist();
    if (skillId && owner.value.startsWith("user:")) {
      const changed = new Set<string>([skillId]);
      if (decision && decision.skillId !== skillId) changed.add(decision.skillId);
      const rows = skillStateRows(state.value, owner.value).filter((row) =>
        changed.has(row.skillId),
      );
      const uuid = UUID_RE.test(input.itemId) ? input.itemId : undefined;
      void useApiHelper()
        .savePracticeMastery({
          rows,
          attempt: {
            skillId,
            difficulty: input.difficulty ?? "core",
            correct: input.correct,
            instanceId: input.generated ? uuid : undefined,
            legacyUuid: input.generated ? undefined : uuid,
          },
        })
        .catch(() => {
          /* localStorage already has the row */
        });
    }
    return decision;
  }

  async function syncSignedIn(userUuid: string | null) {
    if (!userUuid) {
      syncedFor = null;
      syncOwner();
      return;
    }
    if (syncedFor === userUuid || syncingFor === userUuid) {
      await syncing;
      return;
    }
    syncingFor = userUuid;
    syncing = (async () => {
      const userKey = `user:${userUuid}`;
      const curriculumId = curriculumStore.curriculumId;
      const localUser = readState(userKey, curriculumId);
      let guestKey = "guest:local";
      try {
        guestKey = `guest:${getOrCreateGuestId()}`;
      } catch {
        guestKey = "guest:local";
      }
      const guest = readState(guestKey, curriculumId);
      owner.value = userKey;
      try {
        const imported =
          localStorage.getItem(`${IMPORTED_PREFIX}${userUuid}`) === "1";
        let rows: PracticeSkillStateRow[];
        if (!imported) {
          const payload = [
            ...skillStateRows(localUser, userKey),
            ...skillStateRows(guest, guestKey),
          ];
          rows = payload.length
            ? await useApiHelper().importPracticeMastery(payload)
            : await useApiHelper().getPracticeMastery();
          localStorage.setItem(`${IMPORTED_PREFIX}${userUuid}`, "1");
          localStorage.removeItem(storageKey(guestKey));
        } else {
          rows = await useApiHelper().getPracticeMastery();
          const ahead = skillStateRows(localUser, userKey).filter((local) => {
            const remote = rows.find((row) => row.skillId === local.skillId);
            return !remote || local.attempts > remote.attempts;
          });
          if (ahead.length) {
            rows = await useApiHelper().savePracticeMastery({ rows: ahead });
          }
        }
        const skills = skillsFromRows(rows);
        for (const [skillId, local] of Object.entries(localUser.skills)) {
          if (!local.resumeSkillId || !skills[skillId]) continue;
          skills[skillId] = {
            ...skills[skillId],
            resumeSkillId: local.resumeSkillId,
          };
        }
        state.value = {
          ...localUser,
          version: localUser.version,
          curriculumId,
          skills,
        };
        persist();
        syncedFor = userUuid;
      } catch {
        state.value = localUser;
        persist();
      } finally {
        syncingFor = null;
      }
    })();
    await syncing;
  }

  function nextSkill(
    band: PracticeBand,
    subject: string,
    extraSkills: string[] = [],
    grade: number | null = null,
  ) {
    const skills = [
      ...practiceSkillIdsForOffering(band, subject, grade),
      ...extraSkills,
    ];
    return pickNextSkill(state.value, skills);
  }

  /** Signed-in rows are also stored in Postgres. Guests stay in localStorage. */
  function progressRows(): PracticeSkillStateRow[] {
    return skillStateRows(state.value, owner.value);
  }

  function nextTemplate(
    band: PracticeBand,
    subject: string,
    extra?: {
      skill?: string | null;
      excludeId?: string;
      grade?: number | null;
      difficulty?: PracticeDifficulty | null;
    },
  ) {
    const rec = subjectRecord(band, subject);
    const grade = extra?.grade ?? null;
    const skill = extra?.skill ?? nextSkill(band, subject, [], grade);
    const difficulty =
      extra?.difficulty ??
      (skill ? state.value.skills[skill]?.difficulty : undefined);
    return pickNextTemplate(PRACTICE_QUESTION_TEMPLATES, {
      band,
      subject,
      skill,
      seenIds: rec?.seenTemplateIds ?? [],
      excludeId: extra?.excludeId,
      grade,
      difficulty,
    });
  }

  function isProficient(band: PracticeBand, subject: string) {
    return isSubjectProficient(state.value, band, subject);
  }

  function attemptCount(band: PracticeBand, subject: string) {
    return subjectAttemptCount(state.value, band, subject);
  }

  function subjectPct(band: PracticeBand, subject: string) {
    return subjectPercent(state.value, band, subject);
  }

  function seenIds(band: PracticeBand, subject: string) {
    return subjectRecord(band, subject)?.seenTemplateIds ?? [];
  }

  function isPathActive(band: PracticeBand, subject: string) {
    return subjectRecord(band, subject)?.pathActive === true;
  }

  return {
    state,
    percentFor,
    startPath,
    completePath,
    recordOutcome,
    syncSignedIn,
    progressRows,
    nextSkill,
    nextTemplate,
    isProficient,
    attemptCount,
    subjectPct,
    seenIds,
    isPathActive,
    syncOwner,
  };
});

/** Copy guest progress into Postgres on login, and reload it for a signed-in student. */
export async function syncPracticeProgress(userUuid: string | null): Promise<void> {
  await usePracticeMasteryStore().syncSignedIn(userUuid);
}
